import type { OfferModel } from "./types/models/offer";
import type { CampaignBlock } from "./types/models/campaign-block";
import { BLOCK_CATALOG } from "./campaign-blocks";

type Offer = OfferModel.Offer;

/** A live offer as the storefront serves it: optionally with the product id of each of its variations. */
export type LiveOffer = Offer & { variation_products?: Record<string, string> };

/** Active, not deleted, and inside its start/end window at `now`. */
export const isOfferLive = (offer: Offer, now = Date.now()): boolean =>
  offer.status === "active" &&
  !offer.deleted_at &&
  (!offer.starts_at || Date.parse(offer.starts_at) <= now) &&
  (!offer.ends_at || Date.parse(offer.ends_at) >= now);

/** Product ids and variation ids an offer's rules refer to. */
export const offerTargets = (offer: Offer): { productIds: string[]; variationIds: string[] } => {
  const r = offer.offer_rules as unknown as Record<string, unknown>;
  const productIds: string[] = [];
  const variationIds: string[] = [];
  if (typeof r.product_id === "string") productIds.push(r.product_id);
  for (const item of (r.buy_items as { product_id?: string }[] | undefined) ?? []) if (item.product_id) productIds.push(item.product_id);
  for (const item of (r.items as { product_id?: string; product_variation_id?: string }[] | undefined) ?? []) {
    if (item.product_id) productIds.push(item.product_id);
    if (item.product_variation_id) variationIds.push(item.product_variation_id);
  }
  return { productIds, variationIds };
};

/** Live campaign offers (with a landing page) that include the given product. */
export const campaignsForProduct = (offers: Offer[], productId: string, variationIds: string[] = [], now = Date.now()): Offer[] =>
  offers.filter((o) => {
    if (!o.is_campaign || !o.slug || !isOfferLive(o, now)) return false;
    const t = offerTargets(o);
    return t.productIds.includes(productId) || variationIds.some((v) => t.variationIds.includes(v));
  });

/** Short badge text for a product card. */
export const offerBadge = (offer: Offer): string => {
  const r = offer.offer_rules as unknown as Record<string, unknown>;
  switch (offer.type) {
    case "single_product":
      return r.discount_type === "percentage" ? `−${r.discount_value}%` : "Deal";
    case "buy_x_get_y":
      return "Buy & get";
    case "volume_discount":
      return "Bulk deal";
    case "combo_fixed_price":
      return "Combo";
    default:
      return "Offer";
  }
};

/** One-sentence description of what the shopper gets. */
export const describeOffer = (offer: Offer, currency = "TK"): string => {
  const r = offer.offer_rules as unknown as Record<string, unknown>;
  switch (offer.type) {
    case "single_product":
      return `Get ${r.discount_type === "percentage" ? `${r.discount_value}%` : `${currency} ${r.discount_value}`} off this product`;
    case "buy_x_get_y": {
      const sum = (items: unknown) => ((items ?? []) as { quantity?: number }[]).reduce((s, i) => s + (i.quantity || 1), 0);
      return `Buy ${sum(r.buy_items)}, get ${sum(r.get_items)} free or discounted`;
    }
    case "volume_discount":
      return "Buy more, save more: the discount grows with quantity";
    case "combo_fixed_price":
      return `Bundle deal${r.price ? ` for ${currency} ${r.price}` : ""} for the full set`;
    default:
      return "Special offer";
  }
};

/** Campaign block types offered for each offer type (keys match stored CampaignBlock.type). */
export const CAMPAIGN_BLOCKS_BY_OFFER_TYPE = Object.fromEntries(
  Object.entries(BLOCK_CATALOG).map(([type, defs]) => [type, defs.map((d) => d.type)]),
) as Record<OfferModel.OfferType, string[]>;

/**
 * A campaign page must let the shopper order: when the merchant's blocks have no buy box, one is added
 * before the footer (or at the end). CTA buttons link to it (#buy).
 */
export const withOrderForm = (blocks: CampaignBlock[]): CampaignBlock[] => {
  if (blocks.some((b) => b.type === "buy-box")) return blocks;
  const footerAt = blocks.findIndex((b) => b.type === "footer");
  const buy: CampaignBlock = { id: "auto-buy-box", type: "buy-box", variant: "v1", props: {} };
  return footerAt === -1 ? [...blocks, buy] : [...blocks.slice(0, footerAt), buy, ...blocks.slice(footerAt)];
};

/**
 * Campaigns saved before the block editor have only the flat ui_content fields. This turns them into the
 * equivalent block list at render time (nothing is written back), so there is one rendering path.
 */
export const blocksFromUiContent = (offer: Offer): CampaignBlock[] => {
  const ui = offer.ui_content ?? {};
  if (ui.blocks?.length) return withOrderForm(ui.blocks);
  const blocks: CampaignBlock[] = [
    {
      id: "legacy-hero",
      type: "hero",
      variant: ui.hero_image ? "v1" : "v2",
      props: { image: ui.hero_image, title: ui.page_title || offer.name, description: ui.page_description, badge_text: ui.badge_text },
    },
  ];
  if (ui.show_countdown) blocks.push({ id: "legacy-countdown", type: "countdown", props: { label: ui.countdown_label || "Offer ends in" } });
  if (offer.type === "volume_discount") blocks.push({ id: "legacy-tiers", type: "discount-tiers-table", props: { title: "Discount tiers" } });
  if (offer.type === "combo_fixed_price") blocks.push({ id: "legacy-bundle", type: "bundle-price-callout", props: { label: "Bundle price" } });
  if (offer.type === "buy_x_get_y") blocks.push({ id: "legacy-get", type: "get-items-showcase", props: { title: "What you get" } });
  blocks.push(
    { id: "legacy-products", type: "product-grid", variant: "v1", props: { title: offer.type === "buy_x_get_y" ? "Buy these products" : "Products in this campaign", layout: ui.template === "list" ? "list" : "grid" } },
    { id: "legacy-buy", type: "buy-box", variant: "v1", props: {} },
  );
  if (ui.show_terms && ui.terms_text) blocks.push({ id: "legacy-terms", type: "faq", props: { title: "Terms", items: [{ question: "Offer terms", answer: ui.terms_text }] } });
  if (ui.cta_link) blocks.push({ id: "legacy-cta", type: "cta-banner", props: { text: ui.page_subtitle, button_label: ui.cta_label, button_link: ui.cta_link } });
  return blocks;
};
